// ============================================================
// PERRY — THE CODEX
// Records · Achievements · Bestiary · Teachings · The Tale.
// Everything a player might want to look up, in one book that
// survives New Game.
//
// Achievements come from the SHARED Empire engine (achievements.js
// + useAchievements), filtered to the perry:* namespace. The same
// hook, the same store, the same toast — one ledger across every
// title. The Bestiary is gated on records.seen so metOnly entries
// stay a surprise.
// ============================================================

import { useState, useEffect } from "react";
import { GM } from "./theme";
import { EmberDust, MetalRule, KeyGlyph, ensureKeyframes } from "./PerryUI";
import { ENEMIES, TEACHINGS, TALE, GUARD, SPECIES_ORDER, SPECIES, hueOf } from "./perryData";
import { ACHIEVEMENT_RARITY_META, GAME_NAMESPACES, achievementsForGame } from "./achievements";
import { useAchievements } from "./useAchievements";

const KF = `
@keyframes cdxIn  { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
@keyframes cdxLit { 0%,100% { opacity: 0.6; } 50% { opacity: 1; } }
`;

const TABS = [
  { id: "records", label: "RECORDS" },
  { id: "achv", label: "ACHIEVEMENTS" },
  { id: "bestiary", label: "BESTIARY" },
  { id: "teach", label: "TEACHINGS" },
  { id: "tale", label: "THE TALE" },
];

export default function PerryCodex({ uid, records, onBack }) {
  ensureKeyframes("perry-codex-keyframes", KF);
  const [tab, setTab] = useState("records");
  const { earnedIds } = useAchievements(uid);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !e.repeat) onBack(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onBack]);

  const perryAchievements = achievementsForGame("perry");
  const earnedSet = new Set(earnedIds || []);
  const perryEarned = (earnedIds || []).filter((id) => id.startsWith("perry:")).length;
  const tint = GAME_NAMESPACES.perry.tint;

  return (
    <div style={{
      position: "absolute", inset: 0, background: "#050302",
      overflow: "auto", fontFamily: GM.fontUI,
    }}>
      <div style={{ position: "absolute", inset: 0, background: GM.candlelight }} />
      <EmberDust count={14} />

      <div style={{ position: "relative", padding: "28px 20px 50px", display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div style={{ textAlign: "center", marginBottom: 14 }}>
          <div style={{
            fontFamily: GM.fontD, fontSize: "clamp(26px, 6vw, 38px)", letterSpacing: 10,
            color: GM.vellum, textShadow: `0 0 32px ${GM.inkGold}`,
          }}>THE CODEX</div>
          <MetalRule width="min(280px, 66vw)" style={{ margin: "8px auto 0" }} />
        </div>

        {/* ── tabs ── */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "center", marginBottom: 18 }}>
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: "8px 15px", borderRadius: 8, cursor: "pointer",
              background: tab === t.id
                ? "linear-gradient(175deg, #2e1b0e, #1a0f08)"
                : "linear-gradient(175deg, #120a08, #0a0605)",
              border: `1px solid ${tab === t.id ? GM.inkGold : GM.pageBorder}`,
              color: tab === t.id ? "#ffe6c2" : GM.vellumFaint,
              fontFamily: GM.font, fontSize: 11, letterSpacing: 3,
            }}>{t.label}</button>
          ))}
        </div>

        <div style={{ width: "min(880px, 96vw)", animation: "cdxIn 0.4s ease-out" }}>

          {/* ── RECORDS ── */}
          {tab === "records" && (
            <Panel>
              <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }}>
                <Stat k="BEST CHAIN" v={records?.bestChain || 0} c={GM.amethystBright} big />
                <Stat k="PERFECTS" v={records?.perfects || 0} c={GM.inkGoldBright} big />
                <Stat k="PARRIES" v={records?.parries || 0} c={GM.vellum} big />
                <Stat k="RUINS" v={records?.ruins || 0} c={GM.rose} big />
                <Stat k="ROADS WALKED" v={records?.runs || 0} c={GM.vellumDim} />
                <Stat k="ARRIVALS" v={records?.arrivals || 0} c={GM.vellumDim} />
                <Stat k="FALLS" v={records?.falls || 0} c={GM.vellumDim} />
                <Stat k="FOES MET" v={Object.keys(records?.seen || {}).length} c={GM.vellumDim} />
              </div>
              <div style={{ marginTop: 18 }}>
                <SubTitle>THE FOUR ANCHORS</SubTitle>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {SPECIES_ORDER.map((id) => {
                    const done = !!records?.clears?.[id];
                    const s = SPECIES[id];
                    return (
                      <div key={id} style={{
                        padding: "7px 12px", borderRadius: 8, fontSize: 11, letterSpacing: 1,
                        background: done ? "rgba(43,184,168,0.08)" : "rgba(0,0,0,0.3)",
                        border: `1px solid ${done ? GM.amethystDim : GM.pageBorder}`,
                        color: done ? GM.amethystBright : GM.vellumFaint,
                      }}>{done ? "★" : "☆"} {s.name.replace(" Beb", "")}</div>
                    );
                  })}
                </div>
                <div style={{ fontSize: 11, color: GM.vellumFaint, marginTop: 9, lineHeight: 1.5 }}>
                  Records outlive every Soul. Burning a Soul never touches this page.
                </div>
              </div>
            </Panel>
          )}

          {/* ── ACHIEVEMENTS ── */}
          {tab === "achv" && (
            <Panel>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
                <SubTitle>PERRY · {perryEarned} / {perryAchievements.length}</SubTitle>
                <span style={{ fontSize: 10, color: GM.vellumFaint, letterSpacing: 2 }}>
                  EMPIRE-WIDE LEDGER
                </span>
              </div>
              <div style={{ display: "grid", gap: 9, gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))" }}>
                {perryAchievements.map((a) => {
                  const got = earnedSet.has(a.id);
                  const hide = a.hidden && !got;
                  const rar = ACHIEVEMENT_RARITY_META[a.rarity] || {};
                  return (
                    <div key={a.id} style={{
                      padding: "11px 13px", borderRadius: 10, display: "flex", gap: 11, alignItems: "flex-start",
                      background: got ? "linear-gradient(175deg, #241408, #120a08)" : "rgba(0,0,0,0.3)",
                      border: `1px solid ${got ? (rar.tint || tint) : GM.pageBorder}`,
                      opacity: got ? 1 : 0.55,
                    }}>
                      <div style={{
                        fontSize: 22, filter: got ? "none" : "grayscale(1) brightness(0.5)",
                        textShadow: got ? `0 0 12px ${rar.tint || tint}` : "none",
                      }}>{hide ? "❓" : a.icon}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, color: got ? GM.vellum : GM.vellumDim, letterSpacing: 1 }}>
                          {hide ? "???" : a.name}
                        </div>
                        <div style={{ fontSize: 10, color: GM.vellumFaint, marginTop: 3, lineHeight: 1.45 }}>
                          {hide ? "Something is still hidden on this road." : a.desc}
                        </div>
                        {a.rarity && !hide && (
                          <div style={{ fontSize: 9, letterSpacing: 2, marginTop: 4, color: rar.tint || tint }}>
                            {(rar.label || a.rarity).toUpperCase()}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Panel>
          )}

          {/* ── BESTIARY ── */}
          {tab === "bestiary" && (
            <Panel>
              <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))" }}>
                {Object.values(ENEMIES).map((e) => {
                  const met = !!records?.seen?.[e.id];
                  // `veil` added 2026-07-28. `metOnly` used to carry two jobs —
                  // "is not on any road" and "should stay hidden until met" —
                  // and the second road pulled them apart: the Disciple is on
                  // a road now and must still not spoil itself.
                  const veiled = (e.metOnly || e.veil) && !met;
                  return (
                    <div key={e.id} style={{
                      padding: "13px 15px", borderRadius: 10,
                      background: met ? "linear-gradient(175deg, #1c1009, #0d0705)" : "rgba(0,0,0,0.3)",
                      border: `1px solid ${met ? e.tint + "55" : GM.pageBorder}`,
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{
                          fontFamily: GM.font, fontSize: 14, letterSpacing: 2,
                          color: met ? GM.vellum : GM.vellumFaint,
                        }}>{veiled ? "UNMET" : e.name}</span>
                        <span style={{
                          fontSize: 9, letterSpacing: 2, padding: "2px 7px", borderRadius: 5,
                          border: `1px solid ${e.tint}55`, color: e.tint,
                        }}>{e.rung.toUpperCase()}</span>
                      </div>
                      <div style={{ display: "flex", gap: 13, marginTop: 8, fontSize: 11 }}>
                        <span style={{ color: GM.rose }}>❤ {veiled ? "?" : e.anima}</span>
                        <span style={{ color: GM.inkGoldBright }}>⚔ {veiled ? "?" : e.lyt}</span>
                        <span style={{ color: GM.amethystBright }}>◈ LYF {veiled ? "?" : (e.lyf || 0)}</span>
                      </div>
                      <div style={{
                        fontFamily: GM.fontBody, fontSize: 14, lineHeight: 1.55, marginTop: 9,
                        color: veiled ? GM.vellumFaint : GM.vellumDim,
                        fontStyle: veiled ? "italic" : "normal",
                      }}>
                        {veiled ? "You have not stood in front of this one yet." : e.lore}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Panel>
          )}

          {/* ── TEACHINGS ── */}
          {tab === "teach" && (
            <Panel>
              <div style={{
                marginBottom: 18, display: "flex", flexDirection: "column",
                gap: 7, alignItems: "flex-start",
              }}>
                {GUARD.ROWS.map((row, i) => (
                  <div key={i} style={{ display: "flex", gap: 7, alignItems: "center", width: "100%" }}>
                    <span style={{
                      fontSize: 10, letterSpacing: 3, color: GM.vellumFaint,
                      width: 36, textAlign: "right", marginRight: 4,
                    }}>{GUARD.ROW_NAMES[i]}</span>
                    {row.map((k) => (
                      <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <KeyGlyph k={k} size={32} active tint={hueOf(k)} />
                        <span style={{ fontSize: 9, letterSpacing: 1.5, color: hueOf(k), width: 62 }}>
                          {GUARD.HUE_NAMES[k]}
                        </span>
                      </span>
                    ))}
                  </div>
                ))}
              </div>
              {TEACHINGS.map((t, i) => (
                <div key={i} style={{ marginBottom: 18 }}>
                  <SubTitle>{t.title.toUpperCase()}</SubTitle>
                  {t.lines.map((l, j) => (
                    <div key={j} style={{
                      fontFamily: GM.fontBody, fontSize: 16, lineHeight: 1.65,
                      color: GM.vellum, marginBottom: 6, paddingLeft: 12,
                      borderLeft: `2px solid ${GM.inkGoldDim}`,
                    }}>{l}</div>
                  ))}
                </div>
              ))}
            </Panel>
          )}

          {/* ── THE TALE ── */}
          {tab === "tale" && (
            <Panel>
              {TALE.pages.map((p, i) => (
                <div key={i} style={{ marginBottom: 22 }}>
                  <SubTitle>{p.title.toUpperCase()}</SubTitle>
                  <div style={{
                    fontFamily: GM.fontBody, fontSize: 17, lineHeight: 1.75, color: GM.vellum,
                  }}>{p.text}</div>
                </div>
              ))}
              <div style={{
                marginTop: 8, padding: "12px 14px", borderRadius: 10,
                background: "rgba(43,184,168,0.06)", border: `1px solid ${GM.amethystDim}`,
                fontSize: 12, lineHeight: 1.6, color: GM.amethystBright,
              }}>
                The crow on the fencepost is not from here. She is from somewhere else in the
                Empire, and she is only watching. <span style={{ animation: "cdxLit 2s ease-in-out infinite" }}>For now.</span>
              </div>
            </Panel>
          )}
        </div>

        <button onClick={onBack} style={{
          marginTop: 22, padding: "12px 40px", borderRadius: 10, cursor: "pointer",
          background: "linear-gradient(175deg, #1c1009, #0d0705)",
          border: `1px solid ${GM.pageBorder}`, color: GM.vellumDim,
          fontFamily: GM.font, fontSize: 12, letterSpacing: 4,
        }}>← BACK</button>
      </div>
    </div>
  );
}

function Panel({ children }) {
  return (
    <div style={{
      padding: "20px 22px", borderRadius: 14,
      background: "linear-gradient(175deg, #1a0f08 0%, #120a08 55%, #0a0605 100%)",
      border: `1px solid ${GM.pageBorder}`,
      boxShadow: "0 16px 40px rgba(0,0,0,0.6)",
    }}>{children}</div>
  );
}

function SubTitle({ children }) {
  return (
    <div style={{
      fontFamily: GM.font, fontSize: 12, letterSpacing: 4, color: GM.inkGoldBright,
      marginBottom: 10, textShadow: `0 0 12px ${GM.inkGoldDim}`,
    }}>{children}</div>
  );
}

function Stat({ k, v, c, big }) {
  return (
    <div style={{
      padding: "12px 14px", borderRadius: 10,
      background: "rgba(0,0,0,0.32)", border: `1px solid ${GM.pageBorder}`,
    }}>
      <div style={{ fontSize: 9, letterSpacing: 3, color: GM.vellumFaint }}>{k}</div>
      <div style={{
        fontFamily: GM.fontD, fontSize: big ? 30 : 22, color: c, marginTop: 3,
        textShadow: `0 0 16px ${c}55`,
      }}>{v}</div>
    </div>
  );
}
